import React, { useState } from "react";
import { colors2, LoaderOverlay, UI_CAPTION_SEMIBOLD } from "oolib";
import { ButtonPrimary, ButtonTertiary } from "oolib/dist/v2/components/Buttons";
import { TextInput } from "oolib/dist/v2/components/TextInputs";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import {
  extractPlayPackage,
  PACKAGE_NAME_REGEX,
  RATING_OPTIONS,
  REVIEW_COUNT_OPTIONS,
} from "../../DirectDataImport/comps/IntegrationInterfaces/PlayStoreReviewsInterface";
import { IntegrationModalShell } from "../comps/IntegrationModalShell";
import { ManageDatasetsModal } from "../comps/ManageDatasetsModal";
import { caption } from "../comps/common";
import { useExternalConnectionStatus } from "@/utils/react-query-hooks/externalConnections";

/**
 * Play Store Reviews' popups, on IntegrationModalShell. Nothing to connect and
 * nothing to sync, so no connection card or schedule. Same as App Store's,
 * plus an optional rating filter.
 *
 * ImportModal is the import wizard's Play Store step: Continue hands the
 * adapter options to onContinue; the wizard passes back isLoading / error.
 *
 * ManageModal is the shared ManageDatasetsModal: its datasets, delete one or all.
 */

function ImportModal({
  Icon,
  onClose,
  onBack,
  onContinue,
  isLoading,
  error,
  hideBackdrop,
}) {
  // "Do Later" only before Play Store has any dataset; after that, "Discard".
  const { data: conn } = useExternalConnectionStatus("playStoreReviews");
  const hasDatasets = conn?.links?.length > 0;
  const [appInput, setAppInput] = useState("");
  const [count, setCount] = useState(REVIEW_COUNT_OPTIONS[1]); // 300
  const [rating, setRating] = useState(RATING_OPTIONS[0]); // All ratings
  const packageName = extractPlayPackage(appInput);
  const isValid = PACKAGE_NAME_REGEX.test(packageName);
  return (
    <IntegrationModalShell
      title="Import from Play Store"
      Icon={Icon}
      onBack={onBack}
      onClose={onClose}
      hideBackdrop={hideBackdrop}
      footer={
        <>
          <ButtonTertiary onClick={onClose}>
            {hasDatasets ? "Discard" : "Do Later"}
          </ButtonTertiary>
          <ButtonPrimary
            onClick={() =>
              onContinue({
                appId: packageName,
                maxRecords: count.value,
                // 0 = all ratings (BE ignores anything outside 1-5)
                rating: rating.value,
              })
            }
            disabled={!isValid || isLoading}
            isLoading={isLoading}
          >
            Continue
          </ButtonPrimary>
        </>
      }
    >
      {isLoading && (
        <LoaderOverlay loaderText="Fetching data from external source..." />
      )}
      {caption(
        "Import Play Store reviews into your dataset. Each review becomes a row, with its rating, author and any developer reply.",
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <TextInput
          id="appId"
          label="Play Store link or package name"
          placeholder="e.g. https://play.google.com/store/apps/details?id=com.spotify.music"
          value={appInput}
          onChange={(_k, v) => setAppInput(v)}
          readOnly={isLoading}
        />
        {appInput.trim() && !isValid && (
          <UI_CAPTION_SEMIBOLD style={{ color: colors2.red }}>
            Could not find a package name. Paste the Play Store link, or the
            package name itself (looks like com.example.app).
          </UI_CAPTION_SEMIBOLD>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <DropdownSingle
          id="reviewCount"
          label="Maximum reviews to fetch"
          placeholder="Select a limit..."
          options={REVIEW_COUNT_OPTIONS}
          value={count}
          onChange={(_k, v) => setCount(v)}
          readOnly={isLoading}
        />
        {caption("Larger imports take longer to complete.")}
      </div>
      <DropdownSingle
        id="rating"
        label="Filter by rating (optional)"
        placeholder="All ratings"
        options={RATING_OPTIONS}
        value={rating}
        onChange={(_k, v) => setRating(v)}
        readOnly={isLoading}
      />
      {error && (
        <UI_CAPTION_SEMIBOLD style={{ color: colors2.red }}>
          {error}
        </UI_CAPTION_SEMIBOLD>
      )}
    </IntegrationModalShell>
  );
}

// The shared dataset-only manage popup.
const ManageModal = (props) => (
  <ManageDatasetsModal provider="playStoreReviews" {...props} />
);

export const playStore = {
  ImportModal,
  ManageModal,
};
